import { Alert, Box, Typography } from '@mui/material'
import {
  ArrayInput,
  BooleanInput,
  DateInput,
  Edit,
  NumberInput,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
  Toolbar,
  useDefaultTitle,
  useEditContext,
  usePermissions,
  useTranslate,
} from 'react-admin'
import { AlertRulesSection } from '../alerts/AlertRulesSection'
import { AutoSave } from '../components/AutoSave'
import ClientNameEditor from '../owners/ClientNameEditor'
import { PET_SPECIES_VALUES } from '../types'
import { dateInputToDobSeconds, dobSecondsToDateInput } from './dob'
import { canUpdatePetAtAll, canUpdatePetBasics, canUpdatePetClinical } from './petFieldAccess'

// Custom toolbar with AutoSave indicator

// dob conversion lives in ./dob so PetEdit and RegisterOwner (which bypasses
// the dataProvider) share ONE plausibility bound. Re-exported for existing
// importers/tests.
export { dobSecondsToDateInput, dateInputToDobSeconds }

/**
 * One form section: heading, one-line hint, and a responsive field grid (two
 * columns from md). Inputs used to size themselves — 677px, 382px and 740px
 * wide in three consecutive sections — so the form read as a ragged column.
 * Every input now fills its cell; give a child `data-span="full"` for a row
 * of its own (toggles, the conditions list).
 */
const FormSection = ({
  title,
  hint,
  children,
  first,
}: {
  title: React.ReactNode
  hint?: React.ReactNode
  children: React.ReactNode
  first?: boolean
}) => (
  <Box
    component="section"
    sx={{
      width: '100%',
      maxWidth: 880,
      pt: first ? 0 : 3,
      mt: first ? 0 : 1,
      borderTop: first ? 0 : 1,
      borderColor: 'divider',
    }}
  >
    <Typography variant="h6" component="h2">
      {title}
    </Typography>
    {hint && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {hint}
      </Typography>
    )}
    <Box
      sx={{
        mt: 2,
        mb: 1,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        columnGap: 2,
        alignItems: 'start',
        '& > *': { minWidth: 0 },
        '& .MuiFormControl-root': { width: '100%' },
        '& > [data-span="full"]': { gridColumn: '1 / -1' },
      }}
    >
      {children}
    </Box>
  </Box>
)

const PetEditToolbar = () => {
  const translate = useTranslate()
  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <AutoSave debounce={2000} />
    </Toolbar>
  )
}

const PetEdit = () => {
  const translate = useTranslate()
  // Payload strips a field-level-denied field SILENTLY, so an editable control
  // for a field this role cannot write would produce a "Saved" toast over
  // discarded input (L-029). Mirror the server rules and disable instead.
  const { permissions } = usePermissions()
  const role = permissions?.role
  const canEdit = canUpdatePetAtAll(role)
  const canEditBasics = canUpdatePetBasics(role)
  const canEditClinical = canUpdatePetClinical(role)

  // PESSIMISTIC, not optimistic (TLA+ docs/spec/tla/PetEditAutosave.tla P3/P4):
  // optimistic mode runs onSuccess on the next tick and returns from
  // save() before the server answers, so AutoSave's single-flight gate
  // (isSaving) never held -- a second PATCH could go out while the first
  // was open, land first, and lose to the older one; and an edit made
  // while a save was open was reverted on screen and never sent. The
  // form already shows the vet's values, so nothing visible is lost by
  // waiting; "Saved" now means the server has it.
  return (
    <Edit title={<PetTitle />} mutationMode="pessimistic">
      <SimpleForm toolbar={<PetEditToolbar />} resetOptions={{ keepDirtyValues: true }}>
        {(!canEdit || !canEditBasics || !canEditClinical) && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="pet-edit-readonly-notice">
            {translate(
              canEdit ? 'pos.pet_edit.some_fields_read_only' : 'pos.pet_edit.all_fields_read_only',
            )}
          </Alert>
        )}
        <FormSection
          first
          title={translate('pos.pet_edit.pet_information')}
          hint={translate('pos.pet_edit.basic_details_editable')}
        >
          <TextInput
            source="name"
            label={translate('resources.pets.fields.name')}
            disabled={!canEdit}
          />
          {/* Client name is NOT a pets field — it lives on the owner's user
              record, so it writes through its own scoped endpoint rather than
              this form. See ClientNameEditor for why it must stay out of the
              pets PATCH. (Was hardcoded `disabled` until 2026-08-07.) */}
          <ClientNameEditor />
          <SelectInput
            source="species"
            disabled={!canEdit}
            label={translate('resources.pets.fields.species')}
            choices={PET_SPECIES_VALUES.map((id) => ({
              id,
              name: translate(`resources.pets.species_options.${id}`),
            }))}
          />
          <TextInput
            source="breed"
            label={translate('resources.pets.fields.breed')}
            disabled={!canEdit}
          />
          <DateInput
            source="dob"
            disabled={!canEdit}
            label={translate('resources.pets.fields.dob')}
            format={dobSecondsToDateInput}
            parse={dateInputToDobSeconds}
          />
          <SelectInput
            source="gender"
            disabled={!canEdit}
            label={translate('resources.pets.fields.sex')}
            choices={[
              { id: 'Male', name: translate('resources.pets.sex_options.male') },
              { id: 'Female', name: translate('resources.pets.sex_options.female') },
              { id: 'Neutered Male', name: translate('resources.pets.sex_options.neutered_male') },
              { id: 'Spayed Female', name: translate('resources.pets.sex_options.spayed_female') },
              { id: 'Unknown', name: translate('resources.pets.sex_options.unknown') },
            ]}
          />
        </FormSection>

        <FormSection
          title={translate('pos.pet_edit.editable_fields')}
          hint={translate('pos.pet_edit.vet_modifiable_fields')}
        >
          <TextInput
            source="height"
            label={translate('resources.pets.fields.height')}
            disabled={!canEditBasics}
          />
          <TextInput
            source="weight"
            label={translate('resources.pets.fields.weight')}
            disabled={!canEditBasics}
          />
          {/* Glucose range is now derived from hypoglycemia alert rule thresholds */}
        </FormSection>

        {/* α: Chronic-care / vet-managed health panel.  Fields here drive
            AdapetApp's calorie-balance display and the diabetes target-
            glucose UI.  Server-side access controls (Pets/index.ts) restrict
            edits on the vet-only fields to the vet/admin/cms roles. */}
        <FormSection
          title={translate('pos.pet_edit.chronic_care', { _: 'Chronic-care profile' })}
          hint={translate('pos.pet_edit.chronic_care_hint', {
            _: 'Vet-managed clinical fields.  These feed the diabetes glucose-target band and the AdapetApp calorie-balance display.',
          })}
        >
          <NumberInput
            source="weight_kg"
            disabled={!canEditBasics}
            label={translate('resources.pets.fields.weight_kg', { _: 'Weight (kg, exact)' })}
            helperText={translate('pos.pet_edit.weight_kg_hint', {
              _: 'Numeric weight in kilograms.  Auto-filled from the text weight field if empty.',
            })}
            min={0}
            max={150}
            step={0.1}
          />
          <DateInput
            source="birth_date"
            disabled={!canEditBasics}
            label={translate('resources.pets.fields.birth_date', { _: 'Birth date (exact)' })}
            helperText={translate('pos.pet_edit.birth_date_hint', {
              _: 'Calendar date.  Falls back to dob if empty.',
            })}
          />
          <NumberInput
            source="body_condition_score"
            disabled={!canEditBasics}
            label={translate('resources.pets.fields.body_condition_score', {
              _: 'Body condition score (1-9)',
            })}
            min={1}
            max={9}
            step={1}
          />
          <NumberInput
            source="target_kcal_in_override"
            disabled={!canEditClinical}
            label={translate('resources.pets.fields.target_kcal_in_override', {
              _: 'Vet kcal target (kcal/day, optional override)',
            })}
            helperText={translate('pos.pet_edit.kcal_override_hint', {
              _: 'When set, replaces the RER × activity-factor estimate as the daily energy target.',
            })}
            min={0}
            max={5000}
          />
          <Box
            data-span="full"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              columnGap: 4,
              '& .MuiFormControl-root': { width: 'auto' },
            }}
          >
            <BooleanInput
              source="neutered"
              disabled={!canEditBasics}
              label={translate('resources.pets.fields.neutered', { _: 'Neutered / Spayed' })}
            />
            <BooleanInput
              source="hide_glucose_values"
              disabled={!canEditClinical}
              label={translate('resources.pets.fields.hide_glucose_values', {
                _: 'Hide glucose numbers from owner',
              })}
            />
          </Box>
          {/* ArrayInput has no `disabled` prop — gate the iterator's add/remove
              and each child input instead (guard: !canEditClinical). */}
          <Box data-span="full">
            <ArrayInput
              source="chronic_conditions"
              label={translate('resources.pets.fields.chronic_conditions', {
                _: 'Chronic conditions',
              })}
            >
              <SimpleFormIterator
                inline
                disableReordering
                disableAdd={!canEditClinical}
                disableRemove={!canEditClinical}
              >
                <SelectInput
                  source="code"
                  disabled={!canEditClinical}
                  label={translate('resources.pets.fields.condition_code', { _: 'Condition' })}
                  // names are message KEYS: SelectInput translates choice names by default
                  choices={[
                    { id: 'diabetes', name: 'resources.pets.conditions.diabetes' },
                    { id: 'kidney_disease', name: 'resources.pets.conditions.kidney_disease' },
                    { id: 'hyperthyroidism', name: 'resources.pets.conditions.hyperthyroidism' },
                    { id: 'hypothyroidism', name: 'resources.pets.conditions.hypothyroidism' },
                    { id: 'obesity', name: 'resources.pets.conditions.obesity' },
                    { id: 'arthritis', name: 'resources.pets.conditions.arthritis' },
                    { id: 'heart_disease', name: 'resources.pets.conditions.heart_disease' },
                    { id: 'cushings', name: 'resources.pets.conditions.cushings' },
                    { id: 'addisons', name: 'resources.pets.conditions.addisons' },
                    { id: 'epilepsy', name: 'resources.pets.conditions.epilepsy' },
                    { id: 'other', name: 'resources.pets.conditions.other' },
                  ]}
                />
                <DateInput
                  source="diagnosed_on"
                  disabled={!canEditClinical}
                  label={translate('resources.pets.fields.condition_diagnosed_on', {
                    _: 'Diagnosed on',
                  })}
                />
                <TextInput
                  source="notes"
                  disabled={!canEditClinical}
                  label={translate('resources.pets.fields.condition_notes', { _: 'Notes' })}
                  multiline
                />
              </SimpleFormIterator>
            </ArrayInput>
          </Box>
          <>
            <NumberInput
              source="target_glucose_low"
              disabled={!canEditClinical}
              label={translate('resources.pets.fields.target_glucose_low', {
                _: 'Target glucose low (mg/dL)',
              })}
              min={30}
              max={200}
            />
            <NumberInput
              source="target_glucose_high"
              disabled={!canEditClinical}
              label={translate('resources.pets.fields.target_glucose_high', {
                _: 'Target glucose high (mg/dL)',
              })}
              min={80}
              max={400}
            />
          </>
        </FormSection>

        {/* Alert Rules Configuration */}
        <AlertRulesSection />
      </SimpleForm>
    </Edit>
  )
}

const PetTitle = () => {
  const appTitle = useDefaultTitle()
  const { defaultTitle } = useEditContext()
  return (
    <>
      <title>{`${appTitle} - ${defaultTitle}`}</title>
      <span>{defaultTitle}</span>
    </>
  )
}

export default PetEdit
